/* jshint nomen:false */
/**
 * Tile layer for Leaflet that uses the
 * <a href="https://developer.tomtom.com/traffic-api/traffic-api-documentation-traffic-flow/vector-flow-tiles">
 * Vector Flow Tiles</a> from Traffic API.
 *
 * The current vector renderer implementation is based on the
 * [Mapbox GL JS library](https://www.mapbox.com/mapbox-gl-js/api/).
 *
 * @example
 * ```js
 * var ttVectorTrafficLayer = new L.TomTomVectorTrafficFlowLayer({
 *      key: '<Your Traffic Flow API key>',
 *      refresh: 30000,
 *      basePath: '<SDK base path>',
 *      style: 'relative'
 * });
 *
 * map.addLayer(ttVectorTrafficLayer);
 * ```
 *
 * @class TomTomVectorTrafficFlowLayer
 * @namespace L
 * @module Map
 * @extends L.TomTomVectorLayer
 * @constructor
 * @param {"absolute"|"relative"|"relative-delay"} [options.style="absolute"] Specifies the style and type of
 * Traffic Flow
 * @param {String} [options.key] Traffic Flow API key that can be obtained from
 *     <a href="https://developer.tomtom.com">TomTom's developer portal</a>.
 * @param {Integer} [options.refresh=30000] Traffic refresh inteval in milliseconds. The default value is 30 seconds.
 * If a lower value is provided the default value is used.
 */
define([
    'leaflet',
    './vectorLayer',
    '../labelsLayerMixinFactory',
    'security',
    'apiKeyNames'
],
function(
    L,
    VectorLayer,
    labelsLayerMixinFactory,
    security,
    ApiKeyNames
) {
    'use strict';

    var TomTomVectorTrafficFlowLayer = VectorLayer.extend({
        defaultOptions: {
            refresh: 30000,
            style: 'absolute',
            basePath: '/sdk'
        },
        /**
         * Name of the vector traffic flow layer. It can be used to differentiate layers.
         * @property name
         * @default "vectorTrafficFlow"
         * @type String
         */
        name: 'vectorTrafficFlow',
        layerSourceName: 'vectorTilesTraffic',
        refreshIntervalToken: null,
        initialize: function(options) {
            this.validateOptions(options);
            this.layerOptions = this.getLayerOptions(options);
            VectorLayer.prototype.initialize.call(this, this.layerOptions);
            security.setKey(ApiKeyNames.trafficFlow, this.layerOptions.key);
            this.refresh = this.refresh.bind(this);
        },
        onAdd: function(map) {
            VectorLayer.prototype.onAdd.call(this, map);
            this.bringToFront();
            this.startRefresh();
        },
        onRemove: function(map) {
            this.stopRefresh();
            VectorLayer.prototype.onRemove.call(this, map);
        },
        getLayerOptions: function(opts) {
            var layer = this.defaultOptions.style;
            var options = L.extend({}, this.defaultOptions, opts);

            if (['relative', 'relative-delay'].indexOf(options.style) >= 0) {
                layer = options.style;
            }

            return L.extend({}, options, {
                style: 'vectorTrafficFlow',
                layer: layer,
                refresh: Math.max(this.defaultOptions.refresh, options.refresh),
                securityKeyName: ApiKeyNames.trafficFlow,
                vector: {
                    renderer: {
                        refreshExpiredTiles: false
                    }
                },
                styleUrlMapping: {
                    vectorTrafficFlow: {
                        'absolute': options.basePath + '/styles/flow_absolute.json',
                        'relative': options.basePath + '/styles/flow_relative.json',
                        'relative-delay': options.basePath + '/styles/flow_relative-delay.json'
                    }
                }
            });
        },
        validateOptions: function(options) {
            if (options && options.refresh && !Number.isInteger(options.refresh)) {
                throw new Error('The refresh value must be an Integer but ' + typeof options.refresh +
                    ' was provided.');
            }
        },
        getSourceCache() {
            var cache = this._glMap &&
                this._glMap.style &&
                this._glMap.style.sourceCaches &&
                this._glMap.style.sourceCaches[this.layerSourceName];
            if (!cache) {
                // we could just return cache, but this could be any falsy type
                return null;
            }
            return cache;
        },
        startRefresh: function() {
            var refreshRate = this.layerOptions.refresh;
            this.stopRefresh();
            if (refreshRate <= 0) {
                return;
            }
            this.refreshIntervalToken = setInterval(this.refresh, refreshRate);
        },
        refresh: function() {
            var i;
            var cache = this.getSourceCache();
            if (!cache) {
                // don't stop refreshing,
                // this could be recovered in the next call
                return;
            }
            // calling cache.reload() is not efficient enough,
            // because it will call _reloadTile with state different than 'expired'
            // and will not force tile to be downladed again.
            for (i in cache._tiles) {
                if (cache._tiles.hasOwnProperty(i)) {
                    cache._reloadTile(i, 'expired');
                }
            }
            cache.clearTiles();
        },
        stopRefresh: function() {
            if (this.refreshIntervalToken) {
                this.refreshIntervalToken = clearInterval(this.refreshIntervalToken);
            }
        },
        updateOptions: function(options) {
            if (options.style) {
                this.layerOptions = this.getLayerOptions(options);
                VectorLayer.prototype.updateOptions.call(this, this.layerOptions);
                this.startRefresh();
            }
        }
    });

    L.TomTomVectorTrafficFlowLayer = TomTomVectorTrafficFlowLayer
        .extend(labelsLayerMixinFactory('vectorTrafficFlow'));

    L.tomTomVectorTrafficFlowLayer = function tomTomVectorTrafficFlowLayer(options) {
        return new L.TomTomVectorTrafficFlowLayer(options);
    };

    return L.tomTomVectorTrafficFlowLayer;
});



// WEBPACK FOOTER //
// ./display/layers/vector/vectorTrafficFlowLayer.js